import { DateTime } from 'luxon'
import { mapGetters } from 'vuex'

/**
 * A questionnaire bucket object by event
 * 
 * @typedef {Object} EventBucket
 * @property {String} id - The id for the schedule onject in database
 * @property {String} event - The id for the event scheduled
 * @property {Number} time - The time for which the event is scheduled
 * @property {String} type - Whether the bucket is `event` type or `other` type
 * @property {[String]} refEvents - List of eventMessages sent to patient for current event
 * @property {[Object]} refQuestionnaireList - The list of questionnaire answered for current event
 */

/**
 * Mixin used for calculating questions buckets by events 
 * for patients
 * @mixin
 */
export default {

    methods: {
        /**
         * Takes all patient schedules, messages and questionnaire and 
         * buckets questionnaires by event wise
         *
         * @param {[*]} schedules
         * @param {[*]} messages
         * @param {[*]} questionnaires
         * @param {[*]} providerProfileList
         * @returns {[EventBucket]}
         */
        getQuestionnaireBuckets(schedules, messages, questionnaires, tab) {
            try {
                /** A shallow copy of `questionnaires`
                 * as it will be used for splicing
                 */
                let questionnairesCopy = [...questionnaires]
                questionnairesCopy.sort((questionnaireA, questionnaireB) => {
                    return questionnaireB.requested - questionnaireA.requested
                })


                /**
                 * All events with reference ids to messages sent for the
                 * questionnaire for that event
                 * 
                 */
                const scheduleEventMap = schedules.map((scheduleObj) => {
                    /**
                     * Filter messages that have the message id 
                     * in the `scheduleObj` and message has been sent
                     */
                    const scheduledMessages = messages.filter((message) => {
                        return scheduleObj.message.includes(message.id) && ['sent', 'unverified'].includes(message.status)
                    })

                    /**
                     * Flattened `ref_event` inside `scheduleMessages`
                     * @type {[String]}
                     */
                    const refEvents = scheduledMessages.flatMap((message) => {
                        // Check if message has attachment array and filter
                        // for questionnaire type event
                        // Check if `message.ref_event` is in the form of array and filter
                        // for questionnaire type event Messages in `message.ref_type`
                        if(Array.isArray(message.ref_event))
                            return message.ref_event.filter((refEventId, index) => ['survey', 'questionnaire'].includes(message.ref_type[index]))
                        else if(message.attachment && Array.isArray(message.attachment))
                            return message.attachment.filter(({ ref_type }) => ['survey', 'questionnaire'].includes(ref_type)).map(({ ref_event }) => ref_event)
                            // Add `ref_event` if `ref_type` is questionnaire
                        else
                            return ['survey', 'questionnaire'].includes(message.ref_type) ? [message.ref_event] : []
                    })
                    return {
                        id: scheduleObj.id,
                        time: scheduleObj.time,
                        event: scheduleObj.event,
                        provider: scheduleObj.provider ? scheduleObj.provider.split(':')[0] : '',
                        disabled: !scheduleObj.push_emr_enabled,
                        type: 'event',
                        integration: scheduleObj.integration,
                        emrCategory: this.getProfileEmr(scheduleObj.event, scheduleObj.integration ? scheduleObj.integration.combined_notes_push_to_emr : {}),
                        refEvents
                    }
                })
                // Filter out any event with no referernce event messages
                .filter((message) => message.refEvents.length > 0)
                // Sorting reverse chronologicaly by `time` of the event
                .sort((mA, mB) => mB.time - mA.time)

                /**
                 * Map for scheduled questionnaires event wise
                 */
                const scheduleQuestionMap = scheduleEventMap.map((scheduleEvent) => {
                    const { refEvents } = scheduleEvent
                    /**
                     * List of ref Questionnaires extracted
                     * by `refEvents`
                     */
                    let refQuestionnaireList = []

                    // Get questionnaire for each event message in  `refEvents`
                    // spliced from `questionnairesCopy` 
                    refEvents.forEach((refEvent) => {
                        /**
                         * Index of questionnaire by checking for `message` in `questionnaireCopy`
                         */
                        const refQuestionnaireIndex = questionnairesCopy.findIndex((questionnaire) => questionnaire.message === refEvent || questionnaire.id === refEvent)

                        // If questionnaire found for `refEvent`
                        if(refQuestionnaireIndex !== -1) {
                            // Splice refQuestionnaire out of `questionnairesCopy`
                            const refQuestionnaire = questionnairesCopy.splice(refQuestionnaireIndex, 1)
                            // Add emrCategory from integration calculations
                            if(refQuestionnaire[0].integration) {
                                const integration = refQuestionnaire[0].integration.push_to_emr ? refQuestionnaire[0].integration.push_to_emr : {}
                                refQuestionnaire[0].emrCategory = scheduleEvent.emrCategory.map((emrCat) => ({
                                    ...emrCat,
                                    last_pushed: integration[emrCat.id] && integration[emrCat.id].last_pushed ? this.formatDate(integration[emrCat.id].last_pushed) + ' ' + this.formatTime(integration[emrCat.id].last_pushed) : ''
                                }))
                                try {
                                    if(integration) {
                                        const intTime = Math.max(0, ...Object.values(integration).map(( emr ) => emr.last_pushed ? emr.last_pushed : 0))
                                        refQuestionnaire[0].lastPushed = intTime ? this.formatDateTime(intTime) : false
                                    }
                                        
                                } catch (error) {
                                    console.error('Error adding last puushed', error)
                                }
                                
                            }
                            else
                                refQuestionnaire[0].emrCategory = scheduleEvent.emrCategory.map((emrCat) => ({
                                    ...emrCat,
                                    last_pushed: ''
                                }))
                            
                            // and push to `refQuestionnaireList`
                            refQuestionnaireList.push(...refQuestionnaire)
                        }
                    })
 
                    // Return `null` if `refQuestionnaireList` is empty
                    return refQuestionnaireList.length > 0 ? {
                        ...scheduleEvent,
                        refQuestionnaireList
                    } : null
                }).filter((scheduleMessage) => scheduleMessage)

                /**
                 * An array formed at 2n + 1 length of `scheduleQuestionMap`
                 * with values at `i` for `scheduleQuestionMap` representing
                 * values at `2i + 1` for this array and the empty values
                 * are used to store `refQuestionnaires` that come between 
                 * two events
                 */
                let spacedScheduledQuestionMap = Array(2 * scheduleQuestionMap.length + 1).fill(null).map((_, index) => {
                    // Map corresponding value from `scheduleQuestionMap` for odd indexes
                    if(index % 2 !== 0)
                        return scheduleQuestionMap[(index - 1) / 2]
                    else
                        return null
                })
                .map((scheduledQuestions, index, spacedArr) => {
                    // Incase this is an inbetween event value

                    if(scheduledQuestions == null) {
                        // Setting starting and ending date for this window between the events
                        // based on one event before and one event after 
                        // with special cases (starting index and ending index handled)
                        let startTime = 0, endTime = Date.now()
                        if(spacedArr.length == 1)
                            startTime = 0
                        else if(index === 0 && spacedArr[1])
                            startTime = spacedArr[1].time
                        else if(index === spacedArr.length - 1)
                            endTime = spacedArr[spacedArr.length - 2].time
                        else {
                            startTime = spacedArr[index + 1].time
                            endTime = spacedArr[index - 1].time
                        }
                        return {
                            startTime,
                            endTime,
                            id: index,
                            type: 'other',
                            refQuestionnaireList: []
                        }
                    }
                    else
                        return scheduledQuestions
                })


                /**
                 * Total scheduled questions buckets event wise with question between events
                 * (i.e questions that were directly send) also covered
                 */
                const totalScheduledQuestionMap = spacedScheduledQuestionMap.map((scheduledQuestion, spacedIndex) => {
                    const { startTime, endTime } = scheduledQuestion

                    // If its an non event type
                    if(scheduledQuestion.type === 'other') {
                        // Gets questionnaires between `startTime` and `endTime` date for this bucket
                        const questionnairesForOther = questionnairesCopy.filter((questionnaire) => {
                            if(questionnaire.requested) {
                                return questionnaire.requested > startTime && questionnaire.requested <= endTime
                            }
                            else if (!questionnaire.hasOwnProperty('provider_survey')) {
                                return spacedIndex === 0 
                            }
                        })

                        let seprtQuestSurvArr = []

                        if(tab === 'response') {
                            // to separate survey from other collection of survey and questionnaire

                            questionnairesForOther.forEach((questSur) => {
                                const checkProp = questSur.hasOwnProperty('provider_survey') ? 'survey' : 'other'
                                if(!seprtQuestSurvArr.length) {
                                    seprtQuestSurvArr.push({
                                        type: checkProp,
                                        refList: [questSur]
                                    })
                                } else {
                                    if(seprtQuestSurvArr[seprtQuestSurvArr.length-1].type === checkProp) {
                                        seprtQuestSurvArr[seprtQuestSurvArr.length-1].refList.push(questSur)
                                    } else {
                                        seprtQuestSurvArr.push({
                                            type: checkProp,
                                            refList: [questSur]
                                        })
                                    }
                                }
                            })   
                        } else {
                            seprtQuestSurvArr = questionnairesForOther
                        }
                        
                        // Set to this buckets `refQuestionnaireList`
                        scheduledQuestion.refQuestionnaireList = seprtQuestSurvArr
                    }
                    return scheduledQuestion
                })
                // Filter out buckets that don't have any questionnaires
                .filter((scheduledQuestion) => scheduledQuestion.refQuestionnaireList.length > 0)

                return totalScheduledQuestionMap
            } catch (error) {
                console.error('Error getting questionnaire bucket ',error)
                return []
            }
            
        },
        /**
         * Sorting function for sorting in order of `profileQuestionOrder`
         *
         * @param {String} questionA
         * @param {String} questionB
         * @param {*} profileQuestionOrder
         * @returns {Number}
         */
        sortByProfileOrder(questionA, questionB, profileQuestionOrder) {
            return profileQuestionOrder.indexOf(questionA) - profileQuestionOrder.indexOf(questionB)
        },
        /**
         * Finds the questionnaire array for the `eventId`
         * by searching it through the entire `providerProfileList`
         *
         * @param {String} eventId Id of event for this profile questionnaire order is searched
         * @returns {[String]}
         */
        getProfileQuestionaireOrder(eventId) {
            if(this.getAllProviderProfileList && Array.isArray(this.getAllProviderProfileList)) {
                const profile = this.getAllProviderProfileList.find((profile => profile.event.includes(eventId)))
                    if(profile) {
                        const questionnaireList = this.getContentKey('profile', profile.id, 'questionnaire')
                        if(questionnaireList)
                            return questionnaireList.map((questionnaire) => (typeof questionnaire === 'string') ? questionnaire : questionnaire.id)
                    }
            }
            return []
        },
         /**
         * Finds the profile emr for the `eventId`
         * by searching it through the entire `providerProfileList`
         *
         * @param {String} eventId Id of event for this profile questionnaire order is searched
         * @returns {String}
         */
        getProfileEmr(eventId, integrationNotes) {
            try {
                if(this.getAllProviderProfileList && Array.isArray(this.getAllProviderProfileList)) {
                    const profile = this.getAllProviderProfileList.find((profile => profile.event.includes(eventId)))
                        if(profile) {
                            const emrCategory = this.getContentKey('profile', profile.id, 'emr_categories')
                            if(emrCategory)
                                return Object.entries(emrCategory).map(([id, titleObj]) => ({
                                    id,
                                    title: titleObj.title,
                                    last_pushed: integrationNotes[id] && integrationNotes[id].last_pushed ? this.formatDate(integrationNotes[id].last_pushed) + ' ' + this.formatTime(integrationNotes[id].last_pushed) : ''
                                }))
                        }
                }
                return []
            } catch (error) {
                console.error('Problem caculating getProfileEmr', error)
                return []
            }
            
        },
    },
    
    computed: {
        ...mapGetters('provider', [
            'getAllProviderProfileList'
        ]),
        ...mapGetters('content', [
            'getContentType',
            'getContentKey'
        ]),
    }
}

